'use client';

import { ReactNode, useCallback } from 'react';
import { TwoColumnLayout } from '@components/layout/Layouts/TwoColumnLayout';
import { CompanyJobsSidebar } from '@components/features/company-jobs-sidebar/CompanyJobsSidebar';
import { CandidateJobsSidebar } from '@components/features/candidate-jobs-sidebar';
import { SidebarNavigationProvider } from '@components/contexts/SidebarNavigationContext';
import { useHeaderInfoData } from '@components/layout/Header/MainNavbar/data/useHeaderInfoData';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useMissionControlContext } from '@hooks/features/useMissionControlContext';
import type { JobSearchQueryParams } from '@type/filters';

interface LayoutWithTwoColumnsProps {
  children: ReactNode;
  locale?: Locale;
  isAuthenticated?: boolean;
}

const googleApiKey = process.env.GOOGLE_MAPS_CLIENT_API_KEY as string;

/**
 * @description Layout wrapper for pages that need two-column layout.
 *
 * Users can switch between candidate and company contexts using the "Switch Context" button.
 * Context preference is saved in localStorage and persists across sessions.
 *
 * Left sidebar:
 *   - Candidate context: Job search alerts
 *   - Company context: Company navigation
 * Main content: Page content
 */
export const LayoutWithTwoColumns = (props: LayoutWithTwoColumnsProps) => {
  const { children, locale: propLocale, isAuthenticated: propIsAuthenticated } = props;
  const { locale: routerLocale } = useExtendedRouter();
  const locale: Locale = propLocale || routerLocale || 'ro';

  // Get authentication status and user info
  const { headerInfoData, isSessionAuthenticated } = useHeaderInfoData(locale);
  const isAuthenticated = propIsAuthenticated ?? isSessionAuthenticated ?? !!headerInfoData;

  // Get current context from localStorage (candidate or company)
  const { currentContext, switchContext } = useMissionControlContext();

  // On context switch, reset navigation to the /mc base so residual URLs (e.g. /mc/job-add/edit) are cleared.
  const { router } = useExtendedRouter();
  const handleSwitchContext = () => {
    switchContext();
    void router.push('/mc');
  };

  // Opens the Jobs on Map modal. Dynamic import is used to avoid a static circular
  // dependency: JobsMapModal → JobDetailModal → JobDetailHub → LayoutWithTwoColumns.
  // eslint-disable-next-line import/no-cycle
  const handleOpenMap = useCallback((queryParams?: Partial<JobSearchQueryParams>) => {
    if (!googleApiKey) return;
    void (async () => {
      // eslint-disable-next-line import/no-cycle
      const { showJobsMapModal } = await import('@components/templates/job/list/JobsMapModal');
      showJobsMapModal({ googleApiKey, locale, queryParams });
    })();
  }, [locale]);

  // Show company context if selected
  if (currentContext === 'company') {
    const handleJobSelect = (job: ApiSchema<'MyJobCard'>) => {
      void router.push(`/mc/job/${job.slug}/applicants`);
    };
    const handleTagClick = (job: ApiSchema<'MyJobCard'>, status: number) => {
      void router.push(`/mc/job/${job.slug}/applicants?status=${status}`);
    };

    return (
      <TwoColumnLayout
        leftSidebar={(
          <CompanyJobsSidebar
            locale={locale}
            isAuthenticated={isAuthenticated}
            onSwitchContext={handleSwitchContext}
            onJobSelect={handleJobSelect}
            onTagClick={handleTagClick}
            onOpenMap={handleOpenMap}
          />
        )}
        mainContent={children}
        rightSidebar={undefined}
      />
    );
  }

  // Show candidate context (default)
  return (
    <SidebarNavigationProvider initialView="new-search" locale={locale}>
      <TwoColumnLayout
        leftSidebar={(
          <CandidateJobsSidebar
            locale={locale}
            isAuthenticated={isAuthenticated}
            onSwitchContext={handleSwitchContext}
            onOpenMap={handleOpenMap}
          />
        )}
        mainContent={children}
        rightSidebar={undefined}
      />
    </SidebarNavigationProvider>
  );
};
